import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Check, HeartHandshake, Loader2, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";
import { apiError, GENDER_LABELS, PROGRAM_LABELS } from "@/lib/api";
import { trackEvent } from "@/lib/analytics";
import {
  HEYCATCH_EVENTS,
  trackOutcomeOnce,
  trackOutcomeOncePerSession,
} from "@/lib/heycatch";
import { DISCLAIMER_TEXT } from "@/components/layout/Footer";
import { toast } from "sonner";

const PROGRAMS = ["AA", "NA", "CMA", "SMART", "MULTIPLE", "OTHER"];

export default function Register() {
  const { register, user } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({
    account_type: params.get("type") === "sponsor" ? "sponsor" : "sponsee",
    email: "",
    password: "",
    username: "",
    recovery_program: "",
    gender: "",
    city: "",
    state: "",
    country: "",
    accepted_disclaimer: false,
  });

  React.useEffect(() => {
    if (user) navigate("/dashboard", { replace: true });
  }, [user, navigate]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.accepted_disclaimer) {
      setError("Please confirm you understand this is an independent service.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      const u = await register({
        email: form.email,
        password: form.password,
        username: form.username,
        account_type: form.account_type,
        recovery_program: form.recovery_program || null,
        gender: form.gender || null,
        location: { city: form.city || null, state: form.state || null, country: form.country || null },
        accepted_disclaimer: true,
      });
      toast.success("Your free account is ready.");
      trackEvent("signup_completed", {
        account_type: form.account_type,
        user_id: u?.id || null,
      });
      // HeyCatch: the same conversion, guarded by user id so a refresh or a
      // re-render can never report it twice. No account type or profile detail
      // is forwarded - see src/lib/heycatch.js.
      trackOutcomeOnce(`signup:${u?.id}`, HEYCATCH_EVENTS.SIGNUP_COMPLETED);
      navigate(
        params.get("next") ||
          (form.account_type === "sponsor" ? "/become-a-sponsor" : "/sponsors")
      );
      return u;
    } catch (err) {
      setError(apiError(err, "Could not create your account."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 py-12">
      <div className="sm-card p-7 bg-white">
        <div className="flex items-center gap-2 text-xs font-semibold text-[#4B5A60]">
          <span className={step === 1 ? "text-[#1F6F78]" : ""}>1. Who you are</span>
          <span>/</span>
          <span className={step === 2 ? "text-[#1F6F78]" : ""}>2. Your pathway</span>
        </div>
        <h1 className="mt-3 font-display text-3xl font-bold text-[#1F2A2E]">
          Create your free account
        </h1>
        <p className="mt-1.5 text-sm text-[#4B5A60]">
          You never need to share your full legal name. A first name or username is enough.
        </p>

        <form onSubmit={submit} className="mt-6 space-y-5">
          {step === 1 && (
            <>
              <div className="grid gap-3 sm:grid-cols-2">
                {[
                  {
                    value: "sponsee",
                    icon: Search,
                    title: "I'm looking for a sponsor",
                    body: "Browse free, then 20 free messages to start.",
                  },
                  {
                    value: "sponsor",
                    icon: HeartHandshake,
                    title: "I want to sponsor others",
                    body: "Sponsor accounts are free forever, with unlimited messaging.",
                  },
                ].map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => set("account_type", opt.value)}
                    data-testid={`account-type-${opt.value}`}
                    className={`text-left rounded-2xl p-4 border-2 transition-colors bg-white ${
                      form.account_type === opt.value
                        ? "border-[#1F6F78] bg-[#F2FAFA]"
                        : "border-[#E3DED4] hover:border-[#BFD9DB]"
                    }`}
                  >
                    <opt.icon className="w-5 h-5 text-[#1F6F78]" />
                    <p className="mt-2 font-bold text-sm text-[#1F2A2E]">{opt.title}</p>
                    <p className="mt-1 text-xs text-[#4B5A60] leading-5">{opt.body}</p>
                  </button>
                ))}
              </div>

              <div>
                <label className="text-xs font-bold uppercase tracking-wide text-[#4B5A60]">
                  Display name or username
                </label>
                <Input
                  required
                  value={form.username}
                  onChange={(e) => set("username", e.target.value)}
                  data-testid="register-username-input"
                  placeholder="e.g. Mike R."
                  className="mt-1.5 h-11 bg-white border-[#E3DED4]"
                />
                <p className="mt-1 text-xs text-[#7A868B]">
                  This is what other members see. Initials are fine.
                </p>
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className="text-xs font-bold uppercase tracking-wide text-[#4B5A60]">
                    Email
                  </label>
                  <Input
                    type="email"
                    required
                    value={form.email}
                    onChange={(e) => set("email", e.target.value)}
                    data-testid="register-email-input"
                    className="mt-1.5 h-11 bg-white border-[#E3DED4]"
                  />
                </div>
                <div>
                  <label className="text-xs font-bold uppercase tracking-wide text-[#4B5A60]">
                    Password
                  </label>
                  <Input
                    type="password"
                    required
                    minLength={8}
                    value={form.password}
                    onChange={(e) => set("password", e.target.value)}
                    data-testid="register-password-input"
                    className="mt-1.5 h-11 bg-white border-[#E3DED4]"
                  />
                  <p className="mt-1 text-xs text-[#7A868B]">At least 8 characters.</p>
                </div>
              </div>
              <Button
                type="button"
                onClick={() => {
                  if (!form.username || !form.email || form.password.length < 8) {
                    setError("Please fill in a display name, email and a password of 8+ characters.");
                    return;
                  }
                  setError("");
                  trackEvent("signup_started", { account_type: form.account_type });
                  // HeyCatch: once per browser session, so stepping back and
                  // forth through the form does not inflate the funnel.
                  trackOutcomeOncePerSession(
                    "signup_started",
                    HEYCATCH_EVENTS.SIGNUP_STARTED
                  );
                  setStep(2);
                }}
                data-testid="register-next-button"
                className="w-full h-11 bg-[#1F6F78] hover:bg-[#185B62] text-white"
              >
                Continue
              </Button>
            </>
          )}

          {step === 2 && (
            <>
              <div>
                <label className="text-xs font-bold uppercase tracking-wide text-[#4B5A60]">
                  Choose your recovery pathway
                </label>
                <div className="mt-2 flex flex-wrap gap-2">
                  {PROGRAMS.map((p) => (
                    <button
                      key={p}
                      type="button"
                      onClick={() => set("recovery_program", p)}
                      data-testid={`register-program-${p}`}
                      className={`px-3 py-2 rounded-xl text-sm font-semibold border-2 bg-white ${
                        form.recovery_program === p
                          ? "border-[#1F6F78] text-[#1F6F78] bg-[#F2FAFA]"
                          : "border-[#E3DED4] text-[#4B5A60] hover:border-[#BFD9DB]"
                      }`}
                    >
                      {PROGRAM_LABELS[p]}
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid gap-4 sm:grid-cols-3">
                <div>
                  <label className="text-xs font-bold uppercase tracking-wide text-[#4B5A60]">
                    City (optional)
                  </label>
                  <Input
                    value={form.city}
                    onChange={(e) => set("city", e.target.value)}
                    data-testid="register-city-input"
                    className="mt-1.5 h-11 bg-white border-[#E3DED4]"
                  />
                </div>
                <div>
                  <label className="text-xs font-bold uppercase tracking-wide text-[#4B5A60]">
                    State / Region
                  </label>
                  <Input
                    value={form.state}
                    onChange={(e) => set("state", e.target.value)}
                    data-testid="register-state-input"
                    className="mt-1.5 h-11 bg-white border-[#E3DED4]"
                  />
                </div>
                <div>
                  <label className="text-xs font-bold uppercase tracking-wide text-[#4B5A60]">
                    Country
                  </label>
                  <Input
                    value={form.country}
                    onChange={(e) => set("country", e.target.value)}
                    data-testid="register-country-input"
                    className="mt-1.5 h-11 bg-white border-[#E3DED4]"
                  />
                </div>
              </div>
              <p className="text-xs text-[#7A868B]">
                We never display your exact address. Only city, state and country are shown.
              </p>

              <div>
                <label className="text-xs font-bold uppercase tracking-wide text-[#4B5A60]">
                  Gender (optional)
                </label>
                <select
                  value={form.gender}
                  onChange={(e) => set("gender", e.target.value)}
                  data-testid="register-gender-select"
                  className="mt-1.5 w-full h-11 rounded-xl border border-[#E3DED4] bg-white px-3 text-sm"
                >
                  <option value="">Prefer not to say</option>
                  {["man", "woman", "nonbinary"].map((g) => (
                    <option key={g} value={g}>
                      {GENDER_LABELS[g]}
                    </option>
                  ))}
                </select>
              </div>

              <div className="rounded-xl bg-[#F7F4EF] border border-[#E3DED4] p-4">
                <p className="text-xs font-bold text-[#1F2A2E] mb-1">
                  Independent Recovery Community
                </p>
                <p className="text-xs text-[#4B5A60] leading-5">{DISCLAIMER_TEXT}</p>
                <label className="mt-3 flex items-start gap-2 text-sm text-[#1F2A2E]">
                  <input
                    type="checkbox"
                    checked={form.accepted_disclaimer}
                    onChange={(e) => set("accepted_disclaimer", e.target.checked)}
                    data-testid="register-disclaimer-checkbox"
                    className="mt-0.5 w-4 h-4 accent-[#1F6F78]"
                  />
                  <span>
                    I understand this platform is independent, and I agree to the{" "}
                    <Link to="/terms" className="underline font-semibold">
                      Terms of Service
                    </Link>{" "}
                    and{" "}
                    <Link to="/privacy" className="underline font-semibold">
                      Privacy Policy
                    </Link>
                    .
                  </span>
                </label>
              </div>

              {error ? (
                <p data-testid="register-error" className="text-sm text-[#B42318] bg-[#FBE6E4] rounded-lg px-3 py-2">
                  {error}
                </p>
              ) : null}

              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setStep(1)}
                  className="h-11 bg-white border-[#E3DED4]"
                >
                  Back
                </Button>
                <Button
                  type="submit"
                  disabled={busy}
                  data-testid="register-submit-button"
                  className="flex-1 h-11 bg-[#1F6F78] hover:bg-[#185B62] text-white"
                >
                  {busy ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      <Check className="w-4 h-4 mr-2" /> Create my free account
                    </>
                  )}
                </Button>
              </div>
            </>
          )}

          {step === 1 && error ? (
            <p data-testid="register-error" className="text-sm text-[#B42318] bg-[#FBE6E4] rounded-lg px-3 py-2">
              {error}
            </p>
          ) : null}
        </form>

        <p className="mt-5 text-sm text-[#4B5A60] text-center">
          Already a member?{" "}
          <Link to="/login" data-testid="register-to-login-link" className="font-semibold text-[#1F6F78]">
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
}
